import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Droplets, Trash2 } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import useLocalStorage from '../../lib/useLocalStorage';

// ─── Tipos & constantes ───────────────────────────────────────────────────────

interface FlexState {
  precoAlcool: string;
  precoGasolina: string;
  consumoAlcool: string;
  consumoGasolina: string;
}

const INITIAL: FlexState = {
  precoAlcool: '',
  precoGasolina: '',
  consumoAlcool: '',
  consumoGasolina: '',
};

// ─── Lógica de cálculo ────────────────────────────────────────────────────────

type Vantagem = 'alcool' | 'gasolina' | 'igual' | null;

interface FlexResult {
  vantagem: Vantagem;
  ratio: number;
  custoAlcoolKm: number | null;
  custoGasolinaKm: number | null;
  usedConsumption: boolean;
}

function n(v: string): number {
  return parseFloat(v.replace(',', '.'));
}

function calcFlex(state: FlexState): FlexResult | null {
  const pa = n(state.precoAlcool);
  const pg = n(state.precoGasolina);
  if (!pa || !pg || pa <= 0 || pg <= 0) return null;

  const ratio = pa / pg;
  const ca = n(state.consumoAlcool);
  const cg = n(state.consumoGasolina);
  const hasConsumption = ca > 0 && cg > 0;

  if (hasConsumption) {
    const custoAlcoolKm = pa / ca;
    const custoGasolinaKm = pg / cg;
    const vantagem: Vantagem =
      custoAlcoolKm < custoGasolinaKm
        ? 'alcool'
        : custoAlcoolKm > custoGasolinaKm
        ? 'gasolina'
        : 'igual';
    return { vantagem, ratio, custoAlcoolKm, custoGasolinaKm, usedConsumption: true };
  }

  // Regra dos 70%
  const vantagem: Vantagem = ratio < 0.7 ? 'alcool' : ratio > 0.7 ? 'gasolina' : 'igual';
  return { vantagem, ratio, custoAlcoolKm: null, custoGasolinaKm: null, usedConsumption: false };
}

// ─── Componente ───────────────────────────────────────────────────────────────

interface Props {
  className?: string;
}

const inputCls =
  'w-full rounded-xl bg-slate-800 border border-slate-700 px-4 py-3 text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400';

/**
 * Calculadora Flex — álcool vs gasolina.
 * Estado persistido automaticamente no localStorage (chave "gas2:flex").
 * Pode ser embutida em artigos de blog.
 */
const CalculadoraFlex: React.FC<Props> = ({ className = '' }) => {
  const [form, setForm, clearForm] = useLocalStorage<FlexState>('flex', INITIAL);
  const [showConsumption, setShowConsumption] = useState(
    !!(form.consumoAlcool || form.consumoGasolina),
  );

  const set =
    (field: keyof FlexState) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleClear = () => {
    clearForm();
    setShowConsumption(false);
  };

  const result = calcFlex(form);

  const vantagemConfig = {
    alcool: {
      label: '🌿 Álcool (Etanol)',
      color: 'text-emerald-400',
      bg: 'bg-emerald-400/10 border-emerald-400/30',
    },
    gasolina: {
      label: '⛽ Gasolina',
      color: 'text-blue-400',
      bg: 'bg-blue-400/10 border-blue-400/30',
    },
    igual: {
      label: '🔄 Indiferente',
      color: 'text-amber-400',
      bg: 'bg-amber-400/10 border-amber-400/30',
    },
  } as const;

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={`rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 ${className}`}
    >
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
          <Droplets className="h-5 w-5 text-amber-400" strokeWidth={1.8} />
          Calculadora Flex
        </h3>
        <button
          onClick={handleClear}
          title="Limpar dados salvos"
          className="text-slate-500 hover:text-red-400 transition-colors"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>

      <div className="space-y-5">
        {/* Preços */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Preço do Álcool (R$/l)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="Ex: 3.79"
              value={form.precoAlcool}
              onChange={set('precoAlcool')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Preço da Gasolina (R$/l)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="Ex: 5.89"
              value={form.precoGasolina}
              onChange={set('precoGasolina')}
              className={inputCls}
            />
          </div>
        </div>

        {/* Toggle consumo real */}
        <button
          onClick={() => setShowConsumption(v => !v)}
          className="flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${showConsumption ? 'rotate-180' : ''}`}
            strokeWidth={2}
          />
          {showConsumption
            ? 'Ocultar consumo real'
            : 'Adicionar consumo real (resultado mais preciso)'}
        </button>

        <AnimatePresence>
          {showConsumption && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Consumo no Álcool (km/l)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 9.5"
                    value={form.consumoAlcool}
                    onChange={set('consumoAlcool')}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Consumo na Gasolina (km/l)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 13.5"
                    value={form.consumoGasolina}
                    onChange={set('consumoGasolina')}
                    className={inputCls}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Resultado */}
        <AnimatePresence mode="wait">
          {result && result.vantagem && (
            <motion.div
              key={result.vantagem}
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {/* Decisão principal */}
              <div
                className={`rounded-2xl p-6 text-center border ${vantagemConfig[result.vantagem].bg}`}
              >
                <p className="text-sm font-medium text-slate-400 mb-1">
                  Mais vantajoso abastecer com
                </p>
                <p
                  className={`font-display text-3xl font-bold ${vantagemConfig[result.vantagem].color}`}
                >
                  {vantagemConfig[result.vantagem].label}
                </p>
                <p className="text-sm text-slate-400 mt-2">
                  Razão álcool/gasolina:{' '}
                  <span className="text-white font-semibold">
                    {(result.ratio * 100).toFixed(1)}%
                  </span>
                  {!result.usedConsumption && (
                    <span className="text-slate-500 ml-1.5">(regra dos 70%)</span>
                  )}
                </p>
              </div>

              {/* Custo por KM — só se o consumo foi fornecido */}
              {result.usedConsumption &&
                result.custoAlcoolKm != null &&
                result.custoGasolinaKm != null && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-slate-800 p-4 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">
                        Custo/km · Álcool
                      </p>
                      <p className="text-xl font-bold text-emerald-400">
                        R$ {result.custoAlcoolKm.toFixed(3).replace('.', ',')}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-800 p-4 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">
                        Custo/km · Gasolina
                      </p>
                      <p className="text-xl font-bold text-blue-400">
                        R$ {result.custoGasolinaKm.toFixed(3).replace('.', ',')}
                      </p>
                    </div>
                  </div>
                )}

              {!result.usedConsumption && (
                <p className="text-xs text-slate-500 bg-slate-800 rounded-xl px-4 py-3">
                  💡 <strong className="text-slate-400">Dica:</strong> preencha o consumo real
                  do seu carro para um resultado ainda mais preciso — a regra dos 70% é uma
                  estimativa genérica.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {!result && (
          <p className="text-xs text-slate-500 text-center">
            Preencha os preços para ver o resultado.
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default CalculadoraFlex;

